import { Tooltip } from 'antd';
import {
  FARM_DIAGNOSTICS_PRACTICE_LABELS,
  FARM_DIAGNOSTICS_PRACTICE_NO_TILL,
  FARM_DIAGNOSTICS_PRACTICE_CONVENTIONAL_TILL,
  FARM_DIAGNOSTICS_PRACTICE_PASTURE,
  FARM_DIAGNOSTICS_PRACTICE_FALLOW,
  FARM_DIAGNOSTICS_PRACTICE_COVER_CROP,
  FARM_DIAGNOSTICS_ROTATION_CHART_EXCLUDED_SUFFIX,
  FARM_DIAGNOSTICS_ROTATION_CHART_NO_DATA_MESSAGE,
  FARM_DIAGNOSTICS_ROTATION_CHART_NO_PRACTICE_LABEL,
  FARM_DIAGNOSTICS_ROTATION_CHART_Y_AXIS_LABEL,
} from 'constants/uiConstants';
import React from 'react';
import { formatNumber } from '../Insights/summary2/formatNumber';
import type { RotationRow } from './computeRotationEfficiency';
import type { PracticeTag } from '../../../../redux/slices/InsightsSlice';

interface RotationEfficiencyChartProps {
  rows: RotationRow[];
}

const PRACTICE_TAG_FILL_CLASS: Record<PracticeTag, string> = {
  [FARM_DIAGNOSTICS_PRACTICE_NO_TILL]: 'fill-chart-1',
  [FARM_DIAGNOSTICS_PRACTICE_CONVENTIONAL_TILL]: 'fill-chart-2',
  [FARM_DIAGNOSTICS_PRACTICE_PASTURE]: 'fill-chart-3',
  [FARM_DIAGNOSTICS_PRACTICE_FALLOW]: 'fill-chart-4',
  [FARM_DIAGNOSTICS_PRACTICE_COVER_CROP]: 'fill-chart-5',
};
const NO_PRACTICE_FILL_CLASS = 'fill-gray-medium';

// Derived from FARM_DIAGNOSTICS_PRACTICE_LABELS rather than hardcoding each
// label string here — the canonical tag->label mapping lives in uiConstants,
// this just pairs it with a fixed tag->color assignment.
const PRACTICE_LABEL_TO_FILL_CLASS: Record<string, string> = Object.fromEntries(
  (Object.keys(FARM_DIAGNOSTICS_PRACTICE_LABELS) as PracticeTag[]).map((tag) => [
    FARM_DIAGNOSTICS_PRACTICE_LABELS[tag],
    PRACTICE_TAG_FILL_CLASS[tag],
  ])
);

const CHART_H = 260;
const PAD_LEFT = 56;
const PAD_TOP = 12;
const PAD_BOTTOM = 60;
const BAR_SLOT_W = 90;
const BAR_W = 48;
const PLOT_H = CHART_H - PAD_TOP - PAD_BOTTOM;

/**
 * X-axis = paddock, Y-axis = Rotation WUE (kg/mm), bar colored by whichever
 * management practice most recently applied within that paddock's eligible
 * years (the last distinct tag encountered chronologically — an
 * approximation, since practice is tracked per-year but a bar can only
 * carry one color; falls back to neutral gray when nothing was tagged).
 * Only paddocks with a computable Rotation WUE get a bar — never a
 * fabricated zero-height one for a paddock with no/incomplete data.
 */
const RotationEfficiencyChart: React.FC<RotationEfficiencyChartProps> = ({ rows }) => {
  const barRows = rows.filter(
    (r): r is RotationRow & { rotationWue: number } => r.rotationWue != null
  );
  const excludedCount = rows.length - barRows.length;

  if (barRows.length === 0) {
    return (
      <span className="text-sm text-gray-muted">
        {FARM_DIAGNOSTICS_ROTATION_CHART_NO_DATA_MESSAGE}
      </span>
    );
  }

  const chartW = PAD_LEFT + barRows.length * BAR_SLOT_W;
  const maxWue = Math.max(1, ...barRows.map((r) => r.rotationWue));

  const x = (index: number) => PAD_LEFT + index * BAR_SLOT_W + (BAR_SLOT_W - BAR_W) / 2;
  const y = (value: number) => PAD_TOP + PLOT_H - (value / maxWue) * PLOT_H;
  const barHeight = (value: number) => (value / maxWue) * PLOT_H;

  const gridFractions = [0, 0.5, 1];

  const practicesShown = new Set(
    barRows.flatMap((r) => r.managementLabels).filter((l) => PRACTICE_LABEL_TO_FILL_CLASS[l])
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="summary-grid-scroll w-0 min-w-full overflow-x-auto">
        {/* chartW is a continuously-variable value (scales with paddock count)
            with no fixed set of possibilities, so it's set via the style prop
            rather than a Tailwind class — same justified exception this
            codebase already uses for CostByCropTable's dynamic-width bars. */}
        <svg
          viewBox={`0 0 ${chartW} ${CHART_H}`}
          className="h-64"
          style={{ minWidth: chartW }}
        >
          {gridFractions.map((f) => {
            const gy = PAD_TOP + PLOT_H - f * PLOT_H;
            return (
              <g key={f}>
                <line
                  x1={PAD_LEFT}
                  y1={gy}
                  x2={chartW}
                  y2={gy}
                  className="stroke-gray-bright"
                  strokeWidth={1}
                />
                <text
                  x={PAD_LEFT - 8}
                  y={gy + 3}
                  textAnchor="end"
                  className="fill-gray-dark text-[0.6875rem] font-semibold"
                >
                  {formatNumber(maxWue * f)}
                </text>
              </g>
            );
          })}

          {barRows.map((row, i) => {
            const lastLabel = row.managementLabels[row.managementLabels.length - 1];
            const fillClass = lastLabel
              ? (PRACTICE_LABEL_TO_FILL_CLASS[lastLabel] ?? NO_PRACTICE_FILL_CLASS)
              : NO_PRACTICE_FILL_CLASS;
            return (
              <g key={row.paddockId}>
                <Tooltip title={`${row.paddockName}: ${formatNumber(row.rotationWue)} kg/mm`}>
                  <rect
                    x={x(i)}
                    y={y(row.rotationWue)}
                    width={BAR_W}
                    height={barHeight(row.rotationWue)}
                    className={`cursor-help ${fillClass} transition-[filter] hover:brightness-90`}
                  />
                </Tooltip>
                <text
                  x={x(i) + BAR_W / 2}
                  y={CHART_H - PAD_BOTTOM + 16}
                  textAnchor="end"
                  transform={`rotate(-40 ${x(i) + BAR_W / 2} ${CHART_H - PAD_BOTTOM + 16})`}
                  className="fill-gray-dark text-[0.6875rem] font-semibold"
                >
                  {row.paddockName}
                </text>
              </g>
            );
          })}

          <text
            x={-(CHART_H / 2)}
            y={16}
            textAnchor="middle"
            transform="rotate(-90)"
            className="fill-gray-dark text-[0.6875rem] font-semibold"
          >
            {FARM_DIAGNOSTICS_ROTATION_CHART_Y_AXIS_LABEL}
          </text>
        </svg>
      </div>

      {practicesShown.size > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {Array.from(practicesShown).map((label) => (
            <span key={label} className="inline-flex items-center gap-1.5 text-sm text-gray-dark">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-sm ${PRACTICE_LABEL_TO_FILL_CLASS[label].replace('fill-', 'bg-')}`}
              />
              {label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-dark">
            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${NO_PRACTICE_FILL_CLASS.replace('fill-', 'bg-')}`} />
            {FARM_DIAGNOSTICS_ROTATION_CHART_NO_PRACTICE_LABEL}
          </span>
        </div>
      )}

      {excludedCount > 0 && (
        <span className="text-xs text-gray-muted">
          {excludedCount} {FARM_DIAGNOSTICS_ROTATION_CHART_EXCLUDED_SUFFIX}
        </span>
      )}
    </div>
  );
};

export default RotationEfficiencyChart;
